import React from "react";
import { useTheme } from "next-themes";
import { cn } from "@/lib/utils";
import Reveal from "@/components/brams/Reveal";

const offerings = [
  "Logos & identité visuelle",
  "Flyers & dépliants",
  "Cartes de visite",
  "Affiches & menus",
  "Enseignes & bâches",
  "Stickers & supports print",
  "Visuels réseaux sociaux",
  "Maquettes publicitaires",
  "Sites vitrine & e-commerce",
  "Impression clé en main",
];

export default function About() {
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === "dark";

  return (
    <section
      id="about"
      data-testid="about-section"
      className="relative py-28 md:py-36"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-start">
        {/* Image / Logo side */}
        <Reveal as="div" className="lg:col-span-5 order-2 lg:order-1 lg:sticky lg:top-28">
          <div
            className={cn(
              "relative aspect-square overflow-hidden rounded-3xl flex items-center justify-center",
              isDark
                ? "bg-black border border-white/10"
                : "bg-black border border-black/10"
            )}
          >
            <img
              src="https://customer-assets.emergentagent.com/job_brams-portfolio/artifacts/ssol55p1_Logo%20bramsgraphic%20white.png"
              alt="Logo Brams Graphic Design"
              loading="lazy"
              className="w-3/4 h-3/4 object-contain"
              data-testid="about-logo"
            />
            {/* Soft glow */}
            <div
              className="absolute inset-0 pointer-events-none"
              style={{
                background:
                  "radial-gradient(circle at center, rgba(255,255,255,0.06), transparent 60%)",
              }}
            />
            <div className="absolute bottom-5 left-5">
              <span className="text-[10px] uppercase tracking-[0.35em] text-white/70">
                Fondateur · Designer
              </span>
              <div className="font-display font-semibold text-2xl tracking-tighter mt-1 text-white">
                Brams
              </div>
            </div>
          </div>
        </Reveal>

        {/* Copy side */}
        <div className="lg:col-span-7 order-1 lg:order-2">
          <Reveal>
            <span
              className={cn(
                "inline-flex items-center gap-2 text-[10px] md:text-[11px] uppercase tracking-[0.4em] font-medium",
                isDark ? "text-white/60" : "text-black/50"
              )}
            >
              <span className={cn("w-6 h-px", isDark ? "bg-white/40" : "bg-black/40")} />
              À propos
            </span>
          </Reveal>

          <Reveal delay={120}>
            <h2 className="mt-6 font-display font-semibold text-3xl sm:text-4xl md:text-5xl tracking-tighter leading-[1.05]">
              Je crée des visuels qui donnent de{" "}
              <em className="not-italic opacity-60">l&apos;impact</em> à votre entreprise.
            </h2>
          </Reveal>

          <Reveal delay={220}>
            <p
              className={cn(
                "mt-8 text-base md:text-lg font-body font-light leading-relaxed",
                isDark ? "text-white/75" : "text-black/70"
              )}
            >
              Je suis <strong className="font-medium">Brams</strong>, graphiste et web designer
              indépendant. J&apos;accompagne les entreprises, commerces, associations et
              entrepreneurs dans la création de leur identité visuelle et de leurs supports de
              communication.
            </p>
          </Reveal>

          <Reveal delay={280}>
            <p
              className={cn(
                "mt-5 text-base md:text-lg font-body font-light leading-relaxed",
                isDark ? "text-white/75" : "text-black/70"
              )}
            >
              Je ne me limite pas aux logos : je réalise également des flyers, dépliants,
              cartes de visite, affiches, menus de restaurant, enseignes, bâches, stickers,
              visuels pour les réseaux sociaux, maquettes publicitaires, sites internet vitrine
              et e-commerce, ainsi que des créations sur mesure adaptées à chaque projet.
            </p>
          </Reveal>

          <Reveal delay={340}>
            <p
              className={cn(
                "mt-5 text-base md:text-lg font-body font-light leading-relaxed",
                isDark ? "text-white/75" : "text-black/70"
              )}
            >
              J&apos;interviens aussi dans l&apos;impression, afin de vous proposer une solution
              complète, de la conception graphique jusqu&apos;au produit final.
            </p>
          </Reveal>

          <Reveal delay={400}>
            <p
              className={cn(
                "mt-5 text-base md:text-lg font-body font-light leading-relaxed",
                isDark ? "text-white/75" : "text-black/70"
              )}
            >
              Chaque projet est pensé avec précision pour offrir un design moderne,
              professionnel et efficace. Mon objectif est de créer une communication qui attire
              l&apos;attention, inspire confiance et valorise votre image de marque.
            </p>
          </Reveal>

          <Reveal delay={460}>
            <p
              className={cn(
                "mt-5 text-base md:text-lg font-body font-light leading-relaxed",
                isDark ? "text-white/75" : "text-black/70"
              )}
            >
              Du premier croquis jusqu&apos;à l&apos;impression ou à la mise en ligne de votre
              site, je m&apos;occupe de tout.
            </p>
          </Reveal>

          {/* Offerings chips */}
          <Reveal delay={540}>
            <div className="mt-10 flex flex-wrap gap-2.5" data-testid="about-offerings">
              {offerings.map((o) => (
                <span
                  key={o}
                  className={cn(
                    "inline-flex items-center px-3.5 py-1.5 rounded-full text-xs font-body font-light tracking-wide border transition-colors duration-300",
                    isDark
                      ? "border-white/15 bg-white/[0.03] text-white/80 hover:bg-white/[0.08]"
                      : "border-black/15 bg-black/[0.02] text-black/75 hover:bg-black/[0.06]"
                  )}
                >
                  {o}
                </span>
              ))}
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
